/**
 * 첨부파일 섹션 — 모달 안에 넣는 PDF 업로드/목록/삭제 UI.
 *
 * 두 가지 모드:
 *   - mode="live": parentId 가 이미 있음 (수정 모드). DB 에 즉시 업로드/삭제.
 *   - mode="pending": parentId 아직 없음 (신규 등록 모드). File 들을 메모리에
 *     보관만 함. 모달 저장 후 부모 흐름이 자동 업로드.
 *
 * 사용처:
 *   - 영업 등록·수정 모달 (parentKind='sales', kind='분할납품요구서')
 *   - 비상벨 등록·수정 모달 (parentKind='bell', kind='분할납품요구서')
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAttachments,
  uploadAttachment,
  deleteAttachment,
  previewAttachment,
  type AttachmentParentKind,
  type AttachmentMeta,
  type NasAutoSaveResult,
} from '../lib/attachment-api';
import { NasSaveNotice } from './NasSaveNotice';

/** 허용 파일 형식 명세 — 기본 PDF, 발주서 견적서첨부는 엑셀/이미지까지 */
export interface AcceptSpec {
  exts: RegExp;
  attr: string;
  label: string;
}
const PDF_ACCEPT: AcceptSpec = { exts: /\.pdf$/i, attr: 'application/pdf,.pdf', label: 'PDF' };

interface LiveProps {
  mode: 'live';
  parentKind: AttachmentParentKind;
  parentId: string;
  kind: string;
  title?: string;
  readOnly?: boolean;
  /** 단일 첨부 정책 (예: 발주서 1건당 1개). 이미 있으면 confirm 후 replace. */
  single?: boolean;
  /** 허용 파일 형식 (기본 PDF) */
  accept?: AcceptSpec;
  /** 파일이 새로 첨부될 때 호출 (예: 분납서 PDF 자동 파싱 트리거) */
  onUploaded?: (file: File) => void;
}

interface PendingProps {
  mode: 'pending';
  files: File[];
  onChange: (next: File[]) => void;
  title?: string;
  /** 단일 첨부 정책 — 새 파일 선택 시 기존 파일을 덮어씀 (confirm). */
  single?: boolean;
  /** 허용 파일 형식 (기본 PDF) */
  accept?: AcceptSpec;
  /** 파일이 새로 추가될 때 호출 (예: 분납서 PDF 자동 파싱 트리거) */
  onUploaded?: (file: File) => void;
}

type Props = LiveProps | PendingProps;

const MAX = 20 * 1024 * 1024;

function validateFile(file: File, accept: AcceptSpec): string | null {
  if (!accept.exts.test(file.name)) return `${accept.label} 파일만 첨부 가능합니다`;
  if (file.size > MAX) return '20MB 이하의 파일만 가능합니다';
  return null;
}

export function AttachmentSection(props: Props) {
  if (props.mode === 'live') return <LiveSection {...props} />;
  return <PendingSection {...props} />;
}

// ────────────────────────────────────────────────────────────────
// Live mode — 수정 모달 (DB 직접 CRUD)
// ────────────────────────────────────────────────────────────────
function LiveSection({ parentKind, parentId, kind, title = '첨부 파일', readOnly = false, single = false, accept = PDF_ACCEPT, onUploaded }: LiveProps) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const q = useQuery({
    queryKey: ['attachments', parentKind, parentId, kind],
    queryFn: () => listAttachments(parentKind, parentId, kind),
    enabled: Boolean(parentId),
  });

  // 방금 올린 파일의 NAS 보관 결과 — 다음 업로드 전까지 한 줄로 남겨 둔다 (v2.96.0)
  const [nasResult, setNasResult] = useState<NasAutoSaveResult | null>(null);
  // 영업건 세금계산서 PDF 뒷정리 결과 (v2.311.0 · 개발요청 #25 ③) — 발행요청을 닫았나·수신 기록을 이었나 · 다음 업로드 전까지 한 줄
  const [taxNotice, setTaxNotice] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: (vars: { file: File; replace: boolean }) =>
      uploadAttachment(parentKind, parentId, kind, vars.file, { replace: vars.replace }),
    onSuccess: (data, vars) => {
      setNasResult(data.nas ?? null);
      setTaxNotice(data.taxInvoice?.notice ?? null);
      qc.invalidateQueries({ queryKey: ['attachments', parentKind, parentId, kind] });
      if (data.taxInvoice) {
        // 발행요청 이력·수신·처리 내역·미매칭 수가 바뀌었을 수 있다
        void qc.invalidateQueries({ queryKey: ['taxInvoiceRequestHistory'] });
        void qc.invalidateQueries({ queryKey: ['taxInvoiceReceipts'] });
        void qc.invalidateQueries({ queryKey: ['taxInvoiceUnmatchedCount'] });
      }
      if (onUploaded) onUploaded(vars.file);
    },
    onError: (e) => window.alert(`업로드 실패: ${(e as Error).message}`),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAttachment,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['attachments', parentKind, parentId, kind] });
      // 세금계산서 파일을 지우면 이어 둔 수신 기록·발행요청이 되돌아갈 수 있다 (v2.311.0) — 알리고 다시 읽는다
      if (data?.taxInvoice) {
        setTaxNotice(data.taxInvoice.notice);
        void qc.invalidateQueries({ queryKey: ['taxInvoiceRequestHistory'] });
        void qc.invalidateQueries({ queryKey: ['taxInvoiceReceipts'] });
        void qc.invalidateQueries({ queryKey: ['taxInvoiceUnmatchedCount'] });
      }
    },
    onError: (e) => window.alert(`삭제 실패: ${(e as Error).message}`),
  });

  const list: AttachmentMeta[] = q.data ?? [];

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file, accept);
    if (err) { window.alert(err); e.target.value = ''; return; }
    let replace = false;
    if (single && list.length > 0) {
      const ok = window.confirm(
        `이미 첨부된 '${list[0]!.filename}' 이(가) 있습니다.\n기존 발주서는 삭제하고 대체하시겠습니까?`,
      );
      if (!ok) { e.target.value = ''; return; }
      replace = true;
    }
    uploadMut.mutate({ file, replace });
    e.target.value = '';
  }

  // single 모드 + 이미 1개 있으면 추가 버튼 라벨을 "교체"로
  const btnLabel = uploadMut.isPending
    ? '업로드 중…'
    : single && list.length > 0
      ? '🔄 교체'
      : `📎 ${accept.label} 첨부`;

  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-2">
        <label className="ui-label">{title}</label>
        {!readOnly && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={accept.attr}
              onChange={onPickFile}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploadMut.isPending}
              className="btn btn-primary btn-sm"
            >
              {btnLabel}
            </button>
          </>
        )}
      </div>

      {list.length === 0 ? (
        <p className="text-xs text-slate-500 italic">첨부된 파일이 없습니다.</p>
      ) : (
        <ul className="space-y-1">
          {list.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1"
            >
              <button
                type="button"
                onClick={() => previewAttachment(a)}
                className="flex-1 text-left text-blue-700 hover:underline truncate"
                title={`${a.filename} (${Math.round(a.file_size / 1024)} KB)`}
              >
                📄 {a.filename}
              </button>
              <span className="text-slate-400 mx-2 whitespace-nowrap">
                {Math.round(a.file_size / 1024)} KB
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`'${a.filename}' 을(를) 삭제하시겠습니까?`)) {
                      deleteMut.mutate(a.id);
                    }
                  }}
                  disabled={deleteMut.isPending}
                  className="text-rose-500 hover:text-rose-700 disabled:opacity-30 px-1"
                  title="삭제"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* 방금 올린 파일이 NAS 에도 갔는지 (v2.96.0) — 실패 알림 창은 attachment-api 가 따로 띄운다 */}
      <NasSaveNotice nas={nasResult} />
      {taxNotice && (
        <div className="mt-1 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1" data-tax-invoice-notice>🧾 {taxNotice}</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Pending mode — 신규 등록 모달 (File 들 메모리 보관, 부모가 저장 후 일괄 업로드)
// ────────────────────────────────────────────────────────────────
function PendingSection({ files, onChange, title = '첨부 파일', single = false, accept = PDF_ACCEPT, onUploaded }: PendingProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file, accept);
    if (err) { window.alert(err); e.target.value = ''; return; }
    if (single && files.length > 0) {
      const ok = window.confirm(
        `이미 선택된 '${files[0]!.name}' 이(가) 있습니다.\n기존 파일은 제거하고 대체하시겠습니까?`,
      );
      if (!ok) { e.target.value = ''; return; }
      onChange([file]); // 교체
    } else if (single) {
      onChange([file]); // 첫 추가
    } else {
      onChange([...files, file]);
    }
    e.target.value = '';
    if (onUploaded) onUploaded(file);
  }

  function remove(idx: number) {
    onChange(files.filter((_, i) => i !== idx));
  }

  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-2">
        <label className="ui-label">
          {title}
          <span className="ml-1 text-[11px] text-slate-500">(등록 직후 자동 업로드됨)</span>
        </label>
        <input
          ref={inputRef}
          type="file"
          accept={accept.attr}
          onChange={onPickFile}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="btn btn-primary btn-sm"
        >
          📎 {accept.label} 첨부
        </button>
      </div>

      {files.length === 0 ? (
        <p className="text-xs text-slate-500 italic">첨부된 파일이 없습니다.</p>
      ) : (
        <ul className="space-y-1">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1"
            >
              <span className="flex-1 text-slate-700 truncate" title={f.name}>
                📄 {f.name}
              </span>
              <span className="text-slate-400 mx-2 whitespace-nowrap">{Math.round(f.size / 1024)} KB</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-rose-500 hover:text-rose-700 px-1"
                title="제거"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
